/**
 * MobileNav — DS-05/DS-06 folded into an off-canvas drawer (NFR-05).
 *
 * Below 1024px the 240px rail would take a third of the viewport, so it slides
 * in over the content instead of sitting beside it. What it renders is not a
 * second navigation: it reads `useNavigationTree()`, which is the same
 * permission-filtered tree and the same badge counts the desktop rail draws, so
 * a screen a role cannot open is missing from both.
 *
 * It also carries the two commands that live nowhere else once `MenuBar` is off
 * screen — the language toggle and logout. A phone with no way out of a session
 * is not a smaller application, it is a broken one.
 *
 * Anchored to the inline-start edge: `dir=rtl` puts the rail on the right (DS-50),
 * so the drawer that replaces it comes from the right too, and English gets both
 * on the left with no branch on the language here.
 */

import { useEffect } from 'react';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Languages, LogOut, Stethoscope, X } from 'lucide-react';
import { toast } from 'sonner';
import type { CurrentUserDto, Language } from '@nova/shared';
import { Avatar, LiveDot } from '@/components/ui';
import { api } from '@/lib/api';
import { applyLanguage, localized, useLang } from '@/i18n';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { SidebarLink, sidebarRoleLabel, useNavigationTree } from './Sidebar';
import { useOnlineStatus } from './TitleBar';
import { useLogout } from './useLogout';

export interface MobileNavProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** DS-06 — shown under the user's name; the clinic has one branch today. */
  clinicName?: string;
}

export function MobileNav({ open, onOpenChange, clinicName }: MobileNavProps) {
  const { t } = useTranslation('common');
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const { groups, counts, activeTo } = useNavigationTree();
  const online = useOnlineStatus();
  const logout = useLogout();
  const { pathname } = useLocation();

  // The switcher has to answer to what is on screen, not to what the account
  // was last saved as — those diverge for the tick between the toggle and the
  // PATCH, and on the login screen there is no account at all.
  const lang: Language = useLang();
  const userName = user ? localized(lang, user.fullNameAr, user.fullNameEn) : '';
  const nextLang: Language = lang === 'AR' ? 'EN' : 'AR';
  const roleLabel = sidebarRoleLabel(user?.roleNames, t);
  const clinic = clinicName ?? t('clinicName');

  // NFR-03 — the same optimistic write the title bar performs, because the two
  // toggles are one setting and the account has to end up holding it either way.
  const { mutate: persistLanguage } = useMutation({
    mutationFn: (language: Language) =>
      api.patch<CurrentUserDto>('/users/me', { language }).then((r) => r.data),
    onSuccess: (updated) => setUser(updated),
    onError: () => toast.warning(t('language.deviceOnly')),
  });

  // A link inside the drawer navigates and then closes it, but the browser's own
  // back button navigates without touching it — and a drawer still covering the
  // screen the user just went back to reads as a frozen app.
  useEffect(() => {
    if (open) onOpenChange(false);
    // Only the route is a reason to close: reacting to `open` would slam the
    // drawer shut in the same tick the button opened it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  function toggleLanguage(): void {
    applyLanguage(nextLang);
    const current = useAuthStore.getState().user;
    if (!current) return;
    setUser({ ...current, language: nextLang });
    persistLanguage(nextLang);
  }

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-slate-900/60" />

        <RadixDialog.Content
          aria-label={t('a11y.mainNav')}
          aria-describedby={undefined}
          className={cn(
            // `start-0` + `border-e` keep the sheet on the reading edge with its
            // frame facing the content, in either direction (DS-50). A transform
            // cannot be logical, so the entry animation is picked by direction
            // instead — both travel in from off screen on the start edge.
            'fixed inset-y-0 start-0 z-50 flex w-[17rem] max-w-[85vw] flex-col bg-slate-800 shadow-2xl',
            'border-e border-slate-700 ltr:animate-slideInFromStartLtr rtl:animate-slideInFromStartRtl',
          )}
        >
          {/* DS-02 — the same dark identity strip the title bar carries, so the
              drawer reads as the application's own chrome rather than a popup. */}
          <div className="flex shrink-0 items-center gap-2 border-b border-slate-700 bg-slate-900 px-3 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top,0px))]">
            <Stethoscope className="h-5 w-5 shrink-0 text-blue-400" aria-hidden="true" />
            <RadixDialog.Title className="min-w-0 flex-1 truncate text-sm font-bold tracking-wide text-white">
              {clinic}
            </RadixDialog.Title>
            <RadixDialog.Close
              aria-label={t('actions.close')}
              className="-me-1 flex h-touch w-touch shrink-0 items-center justify-center rounded text-slate-300
                         transition hover:bg-slate-700 hover:text-white"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </RadixDialog.Close>
          </div>

          {/* DS-06 — who am I, what am I allowed to be, and where am I. On the
              rail this block sits at the bottom; on a phone the thumb reaches
              the bottom and the eye reads the top, so identity leads. */}
          {user ? (
            <div className="flex shrink-0 items-center gap-2.5 border-b border-slate-700 px-3 py-2.5">
              <Avatar name={userName} size="sm" className="bg-slate-600 text-slate-100" />
              <div className="min-w-0 flex-1 leading-tight">
                <div className="truncate text-xs font-bold text-slate-100">{userName}</div>
                <div className="truncate text-[11px] text-slate-400">{roleLabel}</div>
              </div>
              {/* DS-02/DS-42 — the connection light, which the phone has no title bar room for. */}
              <LiveDot tone={online ? 'emerald' : 'red'} active={online} />
            </div>
          ) : null}

          <nav className="touch-scroll flex-1 overflow-y-auto py-1">
            {groups.map((group) => (
              <div key={group.key} className="pb-1">
                {/* DS-12 */}
                <h2 className="px-4 pb-1 pt-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  {lang === 'EN' ? group.labelEn : group.labelAr}
                </h2>
                <ul>
                  {group.items.map((item) => (
                    <li key={item.key}>
                      <SidebarLink
                        item={item}
                        density="touch"
                        active={activeTo === item.to}
                        count={item.countKey ? counts?.[item.countKey] : undefined}
                        onNavigate={() => onOpenChange(false)}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          {/* The two commands `MenuBar` owns on a desktop and nothing owns here. */}
          <div className="flex shrink-0 items-center gap-2 border-t border-slate-700 px-2 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))]">
            <button
              type="button"
              onClick={toggleLanguage}
              data-testid="mobile-language-toggle"
              data-next-lang={nextLang}
              className="flex min-h-touch flex-1 items-center justify-center gap-1.5 rounded px-3 text-xs
                         font-bold uppercase tracking-widest text-slate-300 transition
                         hover:bg-slate-700 hover:text-white"
            >
              <Languages className="h-4 w-4 shrink-0" aria-hidden="true" />
              {/* The button shows the language it switches to, not the one you are in. */}
              <span className="ltr-num font-mono">{nextLang}</span>
            </button>

            <button
              type="button"
              data-testid="mobile-logout"
              onClick={logout}
              className="flex min-h-touch flex-1 items-center justify-center gap-1.5 rounded px-3 text-xs
                         font-bold text-red-300 transition hover:bg-red-600/20 hover:text-red-200"
            >
              <LogOut className="flip-rtl h-4 w-4 shrink-0" aria-hidden="true" />
              {t('menu.logout')}
            </button>
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export default MobileNav;
